import { backendProvider } from '../../services/backend';
import { fetchCampaignsFromN8N, fetchLeadsFromN8N, fetchFullLeadFromN8N } from '../../utils/n8nDataFetchers';
import type { CampaignMetadata } from '../../types';
import type { CampaignsResult } from '../../utils/n8nDataFetchers';
import { brainiacsService } from '../../components/core/services/brainiacs.service';
import { teamPreferencesService } from '../../services/teamPreferences.service';
import type { TeamPreferences } from '../../services/teamPreferences.service';

const normalizeNameCandidate = (value: unknown): string => {
  const normalized = typeof value === 'string' ? value.trim() : '';
  return normalized && !normalized.includes('@') ? normalized : '';
}

const resolveBestProfileName = (...sources: unknown[]): string => {
  for (const source of sources) {
    const candidate = normalizeNameCandidate(source)
    if (candidate) return candidate
  }
  return ''
}

const isTruthyFlag = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    return normalized === 'true' || normalized === '1' || normalized === 'yes'
  }
  return false
}

const hasOutreachModuleEnabled = (source: any): boolean => {
  if (!source || typeof source !== 'object') return false

  // Check top-level fields with various naming conventions
  const topLevelResult =
    isTruthyFlag(source.outreach_mod) ||
    isTruthyFlag(source.mod_outreach) ||
    isTruthyFlag(source.modOutreach) ||
    isTruthyFlag(source.outreachMod) ||
    isTruthyFlag(source.outreach_module) ||
    isTruthyFlag(source.module_outreach) ||
    isTruthyFlag(source.outreach_enabled) ||
    isTruthyFlag(source.outreachEnabled) ||
    isTruthyFlag(source.has_outreach) ||
    isTruthyFlag(source.hasOutreach)

  if (topLevelResult) return true

  // Check nested objects that might contain the outreach module flag
  const nestedSources = [
    source.modules,
    source.settings,
    source.config,
    source.preferences,
    source.overview,
    source.client,
    source.client_profile,
    source.profile,
  ].filter(Boolean)

  for (const nested of nestedSources) {
    if (typeof nested === 'object') {
      const nestedResult =
        isTruthyFlag(nested.outreach_mod) ||
        isTruthyFlag(nested.mod_outreach) ||
        isTruthyFlag(nested.modOutreach) ||
        isTruthyFlag(nested.outreachMod) ||
        isTruthyFlag(nested.outreach_module) ||
        isTruthyFlag(nested.module_outreach) ||
        isTruthyFlag(nested.outreach_enabled) ||
        isTruthyFlag(nested.outreachEnabled) ||
        isTruthyFlag(nested.has_outreach) ||
        isTruthyFlag(nested.hasOutreach)
      if (nestedResult) return true
    }
  }

  // Fallback: scan all keys for anything outreach/module related
  try {
    const keys = Object.keys(source)
    const outreachKeys = keys.filter(k =>
      k.toLowerCase().includes('outreach') || k.toLowerCase().includes('mod_')
    )
    if (outreachKeys.length > 0) {
      console.log('🔍 [OUTREACH-DEBUG] Found outreach-related keys but none matched:', outreachKeys.map(k => `${k}=${source[k]}`))
    }
  } catch {}

  return false
}

export const appwriteQueryFns = {
  campaigns: {
    all: async (userEmail?: string): Promise<CampaignsResult> => {
      if (!userEmail) return { campaigns: [], teamMembers: [], teamInvitations: [] };
      // Single source of truth for campaigns - only place that calls fetchCampaignsFromN8N
      return fetchCampaignsFromN8N(userEmail);
    },
  },
  leads: {
    // Per-campaign leads fetching (lazy-loaded on demand)
    byCampaign: async (campaignId: string, userEmail: string): Promise<any[]> => {
      if (!campaignId || !userEmail) return [];
      return fetchLeadsFromN8N(campaignId, userEmail);
    },
    detail: async ({ campaignId, leadId, userEmail }: { campaignId: string; leadId: string; userEmail: string }): Promise<any | null> => {
      // Fetch full lead data from N8N (not Appwrite)
      return fetchFullLeadFromN8N(leadId, campaignId, userEmail);
    },
    // Derive counts from campaigns data (passed in, not fetched separately)
    countsFromCampaigns: (campaigns: CampaignMetadata[]): { total: number; campaigns: Record<string, number> } => {
      if (!campaigns || campaigns.length === 0) return { total: 0, campaigns: {} };

      // Build counts object using campaign data
      // Note: leads_to_target contains the total_leads from N8N response
      const campaignCounts = campaigns.reduce((acc, campaign) => {
        acc[campaign.id] = campaign.leads_to_target || 0;
        return acc;
      }, {} as Record<string, number>);

      const total = Object.values(campaignCounts).reduce((sum, count) => sum + count, 0);

      return { total, campaigns: campaignCounts };
    },
  },
  profile: {
    client: async (userEmail: string): Promise<any | null> => {
      // 1. Fetch live credits from API (brainiacsService handles targetEmail internally)
      // 2. Try to fetch client info from users collection (for names/ids)
      // 3. Fallback to auth preferences for current user

      const currentUser = backendProvider.auth.getCurrentUser()
      const targetEmail = backendProvider.auth.getTargetEmail()
      const isAdminImpersonation =
        (currentUser?.labels?.includes('superadmin') || currentUser?.email === 'brainiac@brainiacsbots.com') &&
        Boolean(targetEmail && targetEmail !== currentUser?.email)

      const lookupAuthNameFallback = async (): Promise<string> => {
        if (!isAdminImpersonation || !userEmail?.trim()) return ''

        try {
          const adminUsersResponse = await brainiacsService.customAction('list_all_users', {
            admin_email: currentUser?.email,
          })

          if (!adminUsersResponse.success || !Array.isArray(adminUsersResponse.data)) {
            return ''
          }

          const matchingUser = adminUsersResponse.data.find((entry: any) =>
            String(entry?.email || '').toLowerCase() === userEmail.toLowerCase()
          )

          return resolveBestProfileName(
            matchingUser?.name,
            matchingUser?.full_name,
            matchingUser?.user_name,
            matchingUser?.client_name,
          )
        } catch (error) {
          console.warn('⚠️ Failed to fetch admin auth-name fallback:', error)
          return ''
        }
      }

      // Defensive: Don't query if email is empty/undefined
      if (!userEmail || userEmail.trim() === '') {
        console.warn('⚠️ appwriteQueryFns.profile.client called with empty email, skipping users collection query');
        const creditsResponse = await brainiacsService.getCreditBalance();

        // Fallback to current user prefs
        const prefs = await backendProvider.auth.getPreferences();
        let credits = 0;
        if (creditsResponse.success && creditsResponse.data) {
          const data = Array.isArray(creditsResponse.data) ? creditsResponse.data[0] : creditsResponse.data;
          credits = data?.balance ?? data?.credits ?? 0;
        }

        return {
          id: prefs.client_id || '',
          name: prefs.client_name || '',
          email: '',
          credits,
          seller_ids: prefs.seller_ids ?? [],
          hasOutreachModule: hasOutreachModuleEnabled(prefs),
        };
      }

      // Credits are team-wide: the balance belongs to the team of whoever's
      // email goes on the wire. While impersonating, that must be the TARGET's
      // email (the admin is viewing that team); once impersonation stops, the
      // admin's own email is used again. Passing the admin's email during
      // impersonation showed the ADMIN's team balance in the impersonated
      // header — the exact bug this used to have in reverse.
      const creditsEmail = userEmail || backendProvider.auth.getCurrentUser()?.email || '';

      const [creditsResponse] = await Promise.all([
        brainiacsService.getCreditBalance({ userEmail: creditsEmail }),
      ]);

      const userData = null;
      const authNameFallback = await lookupAuthNameFallback()

      // Extract credits from API response
      let credits = 0;
      if (creditsResponse.success && creditsResponse.data) {
        const data = Array.isArray(creditsResponse.data) ? creditsResponse.data[0] : creditsResponse.data;
        credits = data?.balance ?? data?.credits ?? 0;
      }

      // Profile data comes from auth preferences (Appwrite Auth)
      const prefs = await backendProvider.auth.getPreferences();
      return {
        id: prefs.client_id || '',
        name: resolveBestProfileName(
          authNameFallback,
          prefs.full_name,
          prefs.user_name,
          prefs.client_name,
        ),
        email: userEmail || '',
        credits,
        seller_ids: prefs.seller_ids ?? [],
        hasOutreachModule: hasOutreachModuleEnabled(prefs),
      };
    },
  },
  preferences: {
    user: async (userId: string): Promise<Record<string, any>> => {
      if (!userId) {
        throw new Error('User ID is required to fetch preferences');
      }
      return backendProvider.auth.getPreferences();
    },
    team: async (): Promise<TeamPreferences> => {
      return teamPreferencesService.getPreferences();
    },
  },
  storage: {
    reportExists: async (path: string): Promise<boolean> => {
      if (!path?.trim()) {
        return false;
      }

      return backendProvider.storage.fileExists(path);
    },
    campaignResearchExists: async (campaignId: string): Promise<boolean> => {
      if (!campaignId?.trim()) return false;
      return backendProvider.storage.fileExistsInBucket('campaigns-docs', campaignId);
    },
  },
  admin: {
    listClients: async (adminEmail?: string): Promise<any[]> => {
      // Use server-side list_all_users action to get ALL Appwrite accounts
      // This bypasses collection permissions and returns all users (not just synced ones)
      console.log('🔍 Calling list_all_users action...');
      console.log('🔑 Admin email:', adminEmail);
      // Pass admin_email explicitly to bypass impersonation logic
      const response = await brainiacsService.customAction('list_all_users', {
        admin_email: adminEmail || 'brainiac@brainiacsbots.com'
      });
      console.log('📊 list_all_users success:', response?.data?.length || 0, 'users');

      if (response.success && response.data) {
        console.log(`✅ Got ${response.data.length} users from list_all_users`);
        return Array.isArray(response.data) ? response.data : [];
      }

      // Fallback to empty list if server action fails
      console.warn('⚠️ list_all_users failed:', response.error);
      return [];
    },
  },
};
